import { OFFLINE_ENABLED } from './entities';

/**
 * B14 safeguard #5 — make at-risk offline devices VISIBLE before data is lost.
 * Emits a low-volume "offline device health" heartbeat (Faro + Amplitude) with
 * the persistence state, storage headroom, and queue depth, and escalates to a
 * Faro error when the device is at risk. Both sinks are no-ops when their
 * key/collector env is unset, and the whole thing is inert when offline mode is
 * off, so it's safe to call unconditionally from the offline provider.
 */
const LOW_HEADROOM_PCT = 10;
const HIGH_QUEUE_DEPTH = 50;
const THROTTLE_MS = 5 * 60 * 1000;

let lastEmitAt = 0;
let lastAtRisk: boolean | null = null;

export interface AtRiskInput {
  persisted: boolean | null;
  queueDepth: number;
  conflictDepth: number;
  failedDepth: number;
}

export async function emitAtRiskTelemetry(input: AtRiskInput): Promise<void> {
  if (!OFFLINE_ENABLED) return; // defensive: inert when not opted in
  try {
    let headroomPct: number | null = null;
    const est = await (navigator as any).storage?.estimate?.();
    if (est && typeof est.quota === 'number' && est.quota > 0 && typeof est.usage === 'number') {
      headroomPct = Math.round((1 - est.usage / est.quota) * 100);
    }

    const atRisk =
      input.persisted === false ||
      (headroomPct != null && headroomPct < LOW_HEADROOM_PCT) ||
      input.queueDepth >= HIGH_QUEUE_DEPTH;

    // Throttle to a heartbeat, but always emit immediately when the at-risk
    // state flips so a newly-at-risk device is seen promptly.
    const now = Date.now();
    if (lastAtRisk === atRisk && now - lastEmitAt < THROTTLE_MS) return;
    lastEmitAt = now;
    lastAtRisk = atRisk;

    // Lazy-load the analytics sinks so the offline barrel's STATIC import graph
    // stays free of @grafana/faro-web-sdk (whose ESM deps break Jest's
    // transform). Only loaded when we actually emit. Both sinks are no-ops when
    // their key/collector env is unset.
    const { faro } = await import('../config/faro');
    const { trackAmplitudeEvent } = await import('../config/amplitude');

    // Faro EventAttributes is Record<string,string> — stringify everything.
    faro?.api.pushEvent('offline_device_health', {
      persisted: String(input.persisted),
      headroomPct: String(headroomPct),
      queueDepth: String(input.queueDepth),
      conflictDepth: String(input.conflictDepth),
      failedDepth: String(input.failedDepth),
      atRisk: String(atRisk),
    });
    // Amplitude accepts raw values.
    trackAmplitudeEvent('Offline Device Health', {
      persisted: input.persisted,
      headroomPct,
      queueDepth: input.queueDepth,
      conflictDepth: input.conflictDepth,
      failedDepth: input.failedDepth,
      atRisk,
    });
    // Escalate an at-risk device to an issue, not just an event.
    if (atRisk) faro?.api.pushError?.(new Error('offline_device_at_risk'));
  } catch {
    /* telemetry must never throw into the provider */
  }
}

/** Test-only: reset the throttle between cases. */
export function __resetAtRiskThrottle(): void {
  lastEmitAt = 0;
  lastAtRisk = null;
}
